// react
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// redux
import { useSelector } from 'react-redux';

// mui
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import { default as Grid } from '@mui/material/Grid2';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Divider from '@mui/material/Divider';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';

// project imports
import handleError from 'utils-ts/handle-error';
import callAzureFunction from 'utils-ts/call-azure-function';
import AddMultipleEmails from 'ui-component/AddMultipleEmails';
import { useGetEntityName } from 'hooks-ts/useGetEntity';

import { bookingStatuses } from 'constants/bookings';
import useAppDataSource from 'hooks/useAppDataSource';
import { enqueueSnackbar } from 'notistack';
import { BOOKING_COMMUNICATION_SUBTYPE_KEY, bookingMessageTypes, COMMUNICATION_TYPE_KEY, CONTACT_METHOD_KEY } from '@a1/shared';
import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import parse from 'html-react-parser';

const SendBookingDetailsDialog = ({ open, setOpen, dialogProps, handleCloseSnackbar, snackbarId, displaySnackbarAtParent }) => {
  const appConfig = useSelector((state) => state.appConfig?.data);
  const { appDataSourceId } = useAppDataSource();
  const bookingEntityName = useGetEntityName('booking');
  const respondentEntityName = useGetEntityName('respondent');

  const [respondentDetails, setRespondentDetails] = useState(null);

  const { respondentId, respondent, bookingId, messageType, affiliateSelected } = dialogProps;

  const [isSending, setIsSending] = useState(false);

  // --- Preview State ---
  const [activeTab, setActiveTab] = useState(CONTACT_METHOD_KEY.EMAIL);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // --- End Preview State ---

  // contact methods selected states
  const [emailAddressChecked, setEmailAddressChecked] = useState(false);
  const [contactEmailChecked, setContactEmailChecked] = useState(false);
  const [contactMobilePhoneChecked, setContactMobilePhoneChecked] = useState(false);

  // Send to other emails
  const [otherEmailRecipients, setOtherEmailRecipients] = useState([]);
  const [sendToOtherEmails, setSendToOtherEmails] = useState(false);

  useEffect(() => {
    const getRespondentDetails = async (id) => {
      try {
        const response = await callAzureFunction({ url: `respondents/${id}`, method: 'get', appDataSourceId });
        setRespondentDetails(response?.data);
      } catch (error) {
        handleError(error);
      }
    };
    if (respondent) {
      setRespondentDetails(respondent);
    } else if (respondentId) {
      getRespondentDetails(respondentId);
    }
  }, [respondentId, open, messageType, respondent, appDataSourceId]);

  const fetchPreview = useCallback(
    async (signal) => {
      try {
        setLoadingPreview(true);
        let subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.CONFIRMATION;
        if (affiliateSelected) {
          subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.AFFILIATE_CONFIRMATION;
        } else if (messageType) {
          switch (messageType?.value) {
            case bookingMessageTypes.Upcoming.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.CONFIRMATION;
              break;
            case bookingMessageTypes.Rescheduled.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.RESCHEDULED;
              break;
            case bookingMessageTypes.Canceled.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.CANCELLED;
              break;
            case bookingMessageTypes.DidNotAttend.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.DID_NOT_ATTEND;
              break;
            case bookingMessageTypes.Affiliate.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.AFFILIATE_CONFIRMATION;
              break;
            case bookingMessageTypes.AffiliateCanceled.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.AFFILIATE_CANCELLED;
              break;
            case bookingMessageTypes.AffiliateDidNotAttend.value:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.AFFILIATE_DID_NOT_ATTEND;
              break;
            default:
              subtype = BOOKING_COMMUNICATION_SUBTYPE_KEY.CONFIRMATION;
          }
        }
        const response = await callAzureFunction({
          url: 'communication-preview',
          method: 'post',
          signal,
          data: {
            id: bookingId,
            type: COMMUNICATION_TYPE_KEY.BOOKING,
            subtype,
            contactMethod: activeTab
          },
          appDataSourceId
        });
        setPreview(response.data);
      } catch (error) {
        const message = handleError(error);
        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          enqueueSnackbar({
            variant: 'error',
            message: message || 'Unable to fetch preview.',
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          });
        }
      } finally {
        setLoadingPreview(false);
      }
    },
    [activeTab, affiliateSelected, appDataSourceId, bookingId, messageType]
  );

  // --- Effect to fetch preview ---
  useEffect(() => {
    const controller = new AbortController();

    if (open && bookingId && activeTab) {
      fetchPreview(controller.signal);
    } else {
      setPreview(null);
      return;
    }

    return () => {
      controller.abort();
    };
  }, [open, activeTab, bookingId, fetchPreview]);
  // --- End effect ---

  const handleSend = async () => {
    try {
      setIsSending(true);
      // Only send selectedContactMethods and messageType to backend
      await callAzureFunction({
        url: `/bookings/${bookingId}/send-confirmation`,
        method: 'post',
        data: {
          contactMethods: {
            emailAddress: emailAddressChecked,
            contactEmail: contactEmailChecked,
            contactMobilePhone: contactMobilePhoneChecked,
            otherEmailAddress: sendToOtherEmails
          },
          otherRecipients: sendToOtherEmails ? otherEmailRecipients : [],
          messageType,
          forceSend: true
        },
        appDataSourceId
      });
      handleCloseSnackbar(snackbarId);
      displaySnackbarAtParent(`${bookingEntityName} details have been successfully sent.`, {
        variant: 'success'
      });
    } catch (error) {
      const errMsg = handleError(error);
      displaySnackbarAtParent(`Failed to send. ${errMsg}`, { variant: 'error' });
    } finally {
      setIsSending(false);
      setOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEmailAddressChecked(false);
    setContactEmailChecked(false);
    setContactMobilePhoneChecked(false);
  };

  const isButtonDisabled = !(emailAddressChecked || contactEmailChecked || contactMobilePhoneChecked || sendToOtherEmails);
  const mainHeaderTitle =
    messageType.value.toLowerCase() === bookingStatuses.Upcoming.value
      ? `Send ${bookingEntityName} Details`
      : `Send Updated ${bookingEntityName} Details`;

  const subHeaderTitle =
    messageType.value.toLowerCase() === bookingStatuses.Upcoming.value
      ? `Booking No. ${bookingId} has been successfully scheduled`
      : `${messageType.getSnackbarMessage(bookingEntityName, bookingId)}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md">
        <LinearProgress variant={isSending ? 'indeterminate' : 'buffer'} valueBuffer={100} value={100} />
        <DialogContent>
          <Grid container alignItems="center" mb={3}>
            <Grid spacing={2}>
              <Typography variant="h3" mb={2}>
                {mainHeaderTitle}
              </Typography>
              <Typography variant="h5" mb={1.5}>
                {subHeaderTitle}
              </Typography>
            </Grid>
          </Grid>

          {/* --- Preview Section --- */}
          <Typography variant="h4" sx={{ mb: 1 }}>
            Preview
          </Typography>
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Email" value={CONTACT_METHOD_KEY.EMAIL} />
              <Tab label="SMS" value={CONTACT_METHOD_KEY.SMS} />
            </Tabs>
          </Box>

          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : !preview ? (
            <Typography color="text.disabled" fontStyle="italic" textAlign="center" sx={{ my: 4 }}>
              No preview available.
            </Typography>
          ) : (
            <Box>
              {activeTab === CONTACT_METHOD_KEY.EMAIL && (
                <Box>
                  <Divider sx={{ mb: 1 }}>
                    <Typography color="secondary">Subject</Typography>
                  </Divider>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {preview.emailSubject ? parse(preview.emailSubject) : 'No subject'}
                  </Typography>

                  <Divider sx={{ my: 1 }}>
                    <Typography color="secondary">Body</Typography>
                  </Divider>
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, minHeight: 200, color: 'black' }}>
                    {preview.emailBody ? parse(preview.emailBody) : 'No body content.'}
                  </Box>
                </Box>
              )}

              {activeTab === CONTACT_METHOD_KEY.SMS && (
                <Box>
                  <Divider sx={{ my: 1 }}>
                    <Typography color="secondary">Body</Typography>
                  </Divider>
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, minHeight: 200, color: 'black' }}>
                    {preview?.smsBody ? parse(preview.smsBody.replace(/\n/g, '<br />')) : 'No body content.'}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {/* --- End Preview Section --- */}

          <Divider sx={{ my: 2 }} />

          <TableContainer component={Paper} sx={{ pl: 2, pb: 1 }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell style={{ padding: 0, border: 'none' }}>
                    <Typography sx={{ mb: 1 }} variant="subtitle1">{`${respondentEntityName} Contact Details`}</Typography>
                    <Stack gap={1}>
                      <Typography>
                        Name: {respondentDetails?.firstName} {respondentDetails?.lastName}
                      </Typography>
                      {respondentDetails?.emailAddress && <Typography>Email: {respondentDetails?.emailAddress}</Typography>}
                      {respondentDetails?.contactEmail && <Typography>Contact Email: {respondentDetails?.contactEmail}</Typography>}
                      {respondentDetails?.contactMobilePhone && (
                        <Typography>Contact Mobile Phone: {respondentDetails?.contactMobilePhone}</Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ pb: 1 }} />
          <Typography variant="h4" paddingTop={2}>
            Send to:
          </Typography>
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailAddressChecked}
                  onChange={() => setEmailAddressChecked(!emailAddressChecked)}
                  disabled={!respondentDetails?.emailAddress}
                />
              }
              label="Email Address"
            />

            <Tooltip
              title={
                respondentDetails?.contactEmail === respondentDetails?.emailAddress
                  ? "The recipient's email address is the same with contact email."
                  : !respondentDetails?.contactEmail
                    ? 'The recipient has no contact email.'
                    : "Send to recipient's contact email"
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactEmailChecked}
                    onChange={() => setContactEmailChecked(!contactEmailChecked)}
                    // disable this option if the contact email is the same with email address
                    disabled={!respondentDetails?.contactEmail || respondentDetails?.contactEmail === respondentDetails.emailAddress}
                  />
                }
                hover
                label="Contact Email"
              />
            </Tooltip>

            <FormControlLabel
              control={
                <Checkbox
                  checked={contactMobilePhoneChecked}
                  onChange={() => setContactMobilePhoneChecked(!contactMobilePhoneChecked)}
                  disabled={!respondentDetails?.contactMobilePhone || !appConfig?.generalSettings.sendBySmsEnabled}
                />
              }
              label={appConfig?.generalSettings.sendBySmsEnabled ? 'Mobile Phone' : 'Mobile Phone (Not enabled)'}
              hover
            />

            <Tooltip title={`Other emails to send the updated ${bookingEntityName} details confirmation will also be sent.`}>
              <FormControlLabel
                control={<Checkbox checked={sendToOtherEmails} onChange={(e) => setSendToOtherEmails(e.target.checked)} sx={{ py: 0.5 }} />}
                label="Other Emails"
                hover
              />
            </Tooltip>

            {sendToOtherEmails && (
              <AddMultipleEmails inputWidth="70%" recipients={otherEmailRecipients} setRecipients={setOtherEmailRecipients} />
            )}
          </Stack>
          {!emailAddressChecked && !contactEmailChecked && !contactMobilePhoneChecked && !sendToOtherEmails && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Please select at least one Contact Method.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSend} disabled={isSending || isButtonDisabled} sx={{ minWidth: '100px' }}>
            YES
          </Button>
          <Button onClick={handleClose} disabled={isSending} sx={{ minWidth: '100px' }}>
            NO
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SendBookingDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  dialogProps: PropTypes.object,
  handleCloseSnackbar: PropTypes.func.isRequired,
  snackbarId: PropTypes.string.isRequired,
  displaySnackbarAtParent: PropTypes.func.isRequired
};

export default SendBookingDetailsDialog;
